import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, PressableCard } from '@/components/ui/card';
import {
  ENTRY_PROFILE_CARD,
  ENTRY_PROFILE_CARD_SINGLE_TILL,
  getAccountStatusHint,
  getAccountStatusLabel,
} from '@/content/payments';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import { usePaymentPricing } from '@/hooks/use-payment-pricing';
import { PAYMENT_ACCOUNT_STATUS } from '@/types/payment-account';
import { createThemedStyles, useSolidChip } from '@/hooks/use-theme-colors';

const useStatusVisual = createThemedStyles<
  Record<
    PAYMENT_ACCOUNT_STATUS,
    { icon: React.ComponentProps<typeof Ionicons>['name']; color: string; bg: string }
  >
>((th) => ({
  [PAYMENT_ACCOUNT_STATUS.DRAFT]: { icon: 'time-outline', color: '#FD5001', bg: 'bg-primary/10' },
  [PAYMENT_ACCOUNT_STATUS.VERIFYING]: {
    icon: 'time-outline',
    color: '#FD5001',
    bg: 'bg-primary/10',
  },
  [PAYMENT_ACCOUNT_STATUS.ACTIVE]: {
    icon: 'checkmark-circle-outline',
    color: '#137A63',
    bg: 'bg-success-50',
  },
  [PAYMENT_ACCOUNT_STATUS.ACTION_REQUIRED]: {
    icon: 'alert-circle-outline',
    color: '#D97706',
    bg: 'bg-warning-50',
  },
  [PAYMENT_ACCOUNT_STATUS.RESTRICTED]: {
    icon: 'warning-outline',
    color: '#DC2626',
    bg: 'bg-error-50',
  },
  [PAYMENT_ACCOUNT_STATUS.DISABLED]: {
    icon: 'pause-circle-outline',
    color: th('#737373'),
    bg: 'bg-neutral-100',
  },
}));

/**
 * Card do recebimento automático em Perfil → Pagamentos (T4.5, plano seção
 * 6.1): oferece ativar para quem ainda está no manual e pode ativar
 * (`usePaymentPricing().canActivate`, mesma regra de `about.tsx`); mostra o
 * status resumido para quem já começou ou já ativou. Some por completo
 * quando o recurso está desligado na plataforma e ninguém por aqui ativou —
 * inclusive o wrapper animado (`FadeInDown`), pra não sobrar um respiro
 * vazio no Perfil. Chamador (`profile-screen.tsx`) já garante
 * `canManagePaymentAccount` (ou o caso de caixa único sem ser o dono, ver
 * `singleTillNonOwner`) antes de renderizar (T7.2 — considera o foco, ao
 * contrário de `can('ownPayment')`).
 */
export function PaymentAccountEntryCard({
  className,
  singleTillNonOwner,
}: {
  className?: string;
  /**
   * Caixa único, quem vê não é o dono (T7.2, plano 4.7): nunca é convidado a
   * ativar (só o dono ativa) — sem conta ainda, mostra onde o dinheiro cai
   * em vez do CTA de ativação. Com conta (o dono já ativou), a conta que
   * `usePaymentAccount` traz já É a do negócio (mascarada, `targetProviderId`
   * sempre resolve pro dono em caixa único) — o card de status de baixo
   * cobre esse caso sozinho, sem precisar do flag.
   */
  singleTillNonOwner?: boolean;
}) {
  const solid = useSolidChip();
  const STATUS_VISUAL = useStatusVisual();
  const { data: pricing, isLoading: isPricingLoading } = usePaymentPricing();
  const { data: statusData, isLoading: isAccountLoading } = usePaymentAccount();

  if (isPricingLoading || isAccountLoading || !pricing) return null;

  const account = statusData?.account ?? null;

  if (!account) {
    if (singleTillNonOwner) {
      return (
        <Animated.View entering={FadeInDown.delay(420).duration(400)} className={className}>
          <Card>
            <CardContent className="gap-1 p-4">
              <View className="flex-row items-center gap-3">
                <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                  <Ionicons className="text-primary dark:text-white" name="flash-outline" size={20} />
                </View>
                <ThemedText className="flex-1 font-semibold text-base text-foreground">
                  {ENTRY_PROFILE_CARD_SINGLE_TILL.title}
                </ThemedText>
              </View>
              <ThemedText className="text-sm leading-5 text-neutral-600">
                {ENTRY_PROFILE_CARD_SINGLE_TILL.body}
              </ThemedText>
            </CardContent>
          </Card>
        </Animated.View>
      );
    }
    if (!pricing.enabled || !pricing.canActivate) return null;
    return (
      <Animated.View entering={FadeInDown.delay(420).duration(400)} className={className}>
        <PressableCard
          onPress={() => router.push('/(tabs)/(provider)/payments/about' as never)}
          testID="profile-payments-entry-card"
        >
          <CardContent className="gap-3 p-4">
            <View className="flex-row items-center gap-3">
              <View className="h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                <Ionicons className="text-primary dark:text-white" name="flash-outline" size={20} />
              </View>
              <ThemedText className="flex-1 font-semibold text-base text-foreground">
                {ENTRY_PROFILE_CARD.title}
              </ThemedText>
            </View>
            <ThemedText className="text-sm leading-5 text-neutral-600">
              {ENTRY_PROFILE_CARD.body}
            </ThemedText>
            <Button
              variant="outline"
              title={ENTRY_PROFILE_CARD.ctaLabel}
              onPress={() => router.push('/(tabs)/(provider)/payments/about' as never)}
            />
          </CardContent>
        </PressableCard>
      </Animated.View>
    );
  }

  const visual = STATUS_VISUAL[account.status];
  const due = account.requirementsDue;
  // Quem não é o dono (caixa único) só acompanha: não há o que tocar pra resolver.
  const hint = singleTillNonOwner
    ? null
    : getAccountStatusHint(account.status, !!(due?.pastDue?.length || due?.currentlyDue?.length));

  return (
    <Animated.View entering={FadeInDown.delay(420).duration(400)} className={className}>
      <PressableCard
        onPress={() => router.push('/(tabs)/(provider)/payments/activate' as never)}
        testID="profile-payments-status-card"
      >
        <CardContent className="flex-row items-center gap-3 p-4">
          <View
            className={`h-10 w-10 shrink-0 items-center justify-center rounded-full ${solid.cls(visual.bg)}`}
            style={solid.style(visual.color)}
          >
            <Ionicons name={visual.icon} size={18} color={solid.icon(visual.color)} />
          </View>
          <View className="min-w-0 flex-1 gap-0.5">
            <ThemedText className="font-semibold text-sm text-foreground">
              Recebimento automático
            </ThemedText>
            <ThemedText className="text-xs text-neutral-500">
              {getAccountStatusLabel(account.status)}
            </ThemedText>
            {hint ? (
              <ThemedText className="font-medium text-xs" style={{ color: visual.color }}>
                {hint}
              </ThemedText>
            ) : null}
          </View>
          <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
        </CardContent>
      </PressableCard>
    </Animated.View>
  );
}
